import { array, boolean, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { compact, flatten, get, map, some } from 'lodash/fp';
import { createSelector } from 'reselect';

import { RateGroupSettlementAction } from '@mews/enums';
import { getOrEmptyArray, getStringProp, values } from '@mews/fp';

import { ApplicationState } from '../../../types';
import {
    getImmediatelySettledAmounts,
    getImmediatelySettledAmountsWithPreauthorizations,
} from '../../../utils/settlements/getImmediatelySettledAmounts';
import { getEntities } from '../getEntities';
import { ReservationPrice, ReservationPricesData, Settlement } from './types';

const getReservationPricesEntity = createSelector(getEntities, get('reservationPrices'));

export const getReservationPricesData = createSelector(getReservationPricesEntity, get('data'));
export const getReservationsTotalAmountInPgaCurrency = createSelector(
    getReservationPricesEntity,
    get('reservationsTotalAmountInPgaCurrency')
);
export const getReservationPriceMeta = createSelector(getReservationPricesEntity, get('meta'));
export const areReservationPricesFetching = createSelector(getReservationPriceMeta, some('isFetching'));

interface WithReservationsGroupId {
    reservationGroupId: string;
}

export const getReservationPrices = createSelector<
    ApplicationState,
    WithReservationsGroupId,
    string,
    ReservationPricesData | undefined,
    ReservationPrice[]
>(getStringProp('reservationGroupId'), getReservationPricesData, getOrEmptyArray);

export const getReservationsPrices = createSelector(
    getReservationPricesData,
    getStringProp('identifier'),
    (prices, identifier) =>
        pipe(
            prices ?? {},
            values,
            array.flatten,
            array.filter((reservationPrice) => reservationPrice.identifier === identifier)
        )
);

const getReservationsSettlements = createSelector(
    getReservationPrices,
    flow<[Array<ReservationPrice>], Array<Array<Settlement> | null>, Array<Array<Settlement>>>(
        map('settlements'),
        compact
    )
);

export const getReservationsTotalAmounts = createSelector(getReservationPrices, map('totalAmount'));

export const getFlatImmediatelySettledAmounts = createSelector(
    getReservationsSettlements,
    flow(flatten, getImmediatelySettledAmounts)
);

const getReservationsSettlementsFlatten = flow(getReservationsSettlements, array.flatten);

const getReservationsSettlementsActions = flow(
    getReservationsSettlementsFlatten,
    array.map(({ action }) => action),
    array.uniq(string.Eq),
    array.sort(string.Ord)
);

export const getFlatImmediatelySettledAmountsWithPreauthorizations = createSelector(
    getReservationsSettlementsFlatten,
    getReservationsSettlementsActions,
    (settlements, actions) =>
        pipe(
            array.getEq(string.Eq).equals(record.keys(RateGroupSettlementAction), actions),
            boolean.match(
                () => settlements,
                () =>
                    pipe(
                        settlements,
                        array.filter(({ action }) => action === RateGroupSettlementAction.ChargeCreditCard)
                    )
            ),
            getImmediatelySettledAmountsWithPreauthorizations
        )
);
